import { useState } from 'react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import isValid from 'date-fns/isValid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import enUS from 'date-fns/locale/en-US';
import { fontFamily } from '@design-system/tokens';

type Props = {
  open: boolean;
  initialWhen?: Date | null;
  actionLabel?: string;
  onConfirm: (when: Date) => void;
  onDismiss: () => void;
};

const nextHour = (): Date => {
  const when = new Date();
  when.setMinutes(0, 0, 0);
  when.setHours(when.getHours() + 1);
  return when;
};

// Both pickers publish an Invalid Date on unparseable typed input, and an
// Invalid Date is truthy, so a rejected value must never reach state: combine
// would read NaN off it and every later pick would re-poison the result.
const combine = (day: Date, time: Date): Date | null => {
  if (!isValid(day) || !isValid(time)) return null;
  const when = new Date(day);
  when.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return when;
};

const fieldStyles = {
  fontFamily: fontFamily.visuelt,
  '>div': {
    fontFamily: fontFamily.visuelt,
    height: '48px',
    borderRadius: '10px',
  },
};

export const SchedulePostModal = ({
  open,
  initialWhen,
  actionLabel = 'Schedule',
  onConfirm,
  onDismiss,
}: Props) => {
  const [when, setWhen] = useState<Date>(
    initialWhen && isValid(initialWhen) ? initialWhen : nextHour(),
  );

  const keep = (next: Date | null) => {
    if (next) setWhen(next);
  };

  const isFuture = when.getTime() > Date.now();

  return (
    <Modal open={open} onClose={onDismiss}>
      <div className="absolute left-1/2 top-1/2 w-[326px] -translate-x-1/2 -translate-y-1/2 rounded-[10px] bg-white shadow-[0px_0px_1px_#EAECF0,0px_32px_64px_-12px_rgba(29,41,57,0.08)] md:w-[560px]">
        <div className="flex items-center justify-between p-8 pb-6">
          <h4 className="text-xl font-bold">Schedule post</h4>
          <button type="button" aria-label="Close" onClick={onDismiss}>
            <FontAwesomeIcon icon={faXmark} className="text-xl" />
          </button>
        </div>

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
          <div className="flex flex-col gap-4 px-8 pb-8 md:flex-row">
            <DatePicker
              value={when}
              disablePast
              onChange={(day) => day && keep(combine(day, when))}
              sx={{ ...fieldStyles, flex: 1 }}
              slotProps={{
                textField: { inputProps: { 'aria-label': 'Publish date' } },
              }}
            />
            <TimePicker
              value={when}
              onChange={(time) => time && keep(combine(when, time))}
              sx={{ ...fieldStyles, flex: 1 }}
              slotProps={{
                textField: { inputProps: { 'aria-label': 'Publish time' } },
              }}
            />
          </div>
        </LocalizationProvider>

        <div className="flex items-center justify-end gap-4 border-t border-[#ECEDF2] p-8">
          {!isFuture && (
            <span className="mr-auto text-sm text-[#E5322D]">
              Pick a time in the future
            </span>
          )}
          <button
            type="button"
            onClick={onDismiss}
            className="text-base font-medium text-[#131313]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isFuture}
            onClick={() => when.getTime() > Date.now() && onConfirm(when)}
            className="rounded-[10px] bg-[#131313] px-5 py-2.5 text-base font-medium text-white disabled:bg-[#EEEEEE] disabled:text-[#9E9E9E]"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};
